/**
 * useLiveListenControls - Live listen/call control state and actions
 *
 * Extracts the live-mode listen button orchestration from App.tsx so the
 * hosted shell can keep the live control surface separate from rendering.
 */

import { Dispatch, MutableRefObject, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';

import CamlinSocketHandler from '../../channels/voice/CamlinSocketHandler';
import { hangupNow } from '../../channels/voice/voiceHandlers';
import type { AvatarSessionContext, AvatarSessionStatus } from '../../session';
import { sttFromMic } from '../utils/audioUtils';
import { usesBrowserSttOnLiveSurface } from '../utils/liveControllerUtils';
import type { RequestLiveSessionFn } from './useLiveSessionRequest';

interface LiveListenControlClient {
  sendInput: (...args: any[]) => void;
  sendSessionContext?: (context: Record<string, unknown>) => void;
}

interface LiveListenControlRefs {
  liveAutoListenArmedRef: MutableRefObject<boolean>;
  liveAutoListenPendingRef: MutableRefObject<boolean>;
  liveAutoListenTimerRef: MutableRefObject<number | null>;
  liveClientRef: MutableRefObject<LiveListenControlClient | null>;
  liveManualHangupRef: MutableRefObject<boolean>;
  livePromptAudioRequestRef: MutableRefObject<number>;
  livePromptListeningRef: MutableRefObject<boolean>;
  livePromptTextRef: MutableRefObject<string>;
  liveSessionContextRef: MutableRefObject<AvatarSessionContext | null>;
  liveSpeakingRef: MutableRefObject<boolean>;
  liveThinkingRef: MutableRefObject<boolean>;
  liveVoiceAttemptRef: MutableRefObject<number>;
  liveVoicePromptStartedRef: MutableRefObject<boolean>;
  liveVoiceRetryCountRef: MutableRefObject<number>;
}

export interface UseLiveListenControlsOptions {
  refs: LiveListenControlRefs;
  activeListen: boolean;
  handleInputExternally?: boolean;
  avatarRef: RefObject<unknown>;
  currentSession: any;
  clearLiveAutoListenTimer: () => void;
  clearLivePlaybackCountdown: () => void;
  clearLiveSpeakingTimeout: () => void;
  clearLiveVoiceRetryTimer: () => void;
  dispatchAvatarEvent: (eventName: string, detail: unknown) => void;
  ensureVoiceBrowserAccess: (requiresMicrophone: boolean) => Promise<boolean>;
  hangupDebounceTime: number;
  isAvatarReady: boolean;
  isCallActive: boolean;
  isInVoiceCall: boolean;
  isMobileCall: boolean;
  /**
   * True for the runtime host's `/visual` route — Visual IVR. The phone has
   * the call (mic + speaker) and the browser only renders the avatar; it
   * must never open the local microphone or run STT against laptop audio.
   * Without this gate the browser picks up phone audio bleeding through
   * the device mic, transcribes garbage ("Madonna thoroughly", etc.), and
   * sends it back to the runtime as if the user had spoken it.
   */
  isVisualOnly: boolean;
  isVoiceInitialized: boolean;
  isWebrtcCall: boolean;
  lastHangupTime: number;
  liveConnectRequested: boolean;
  liveConnectionError: string | null;
  liveModeActive: boolean;
  liveSessionStatus?: AvatarSessionStatus;
  liveVoiceCallEnabled: boolean;
  playing: boolean;
  record: boolean;
  refreshUIButtonsOnCallEnd: () => void;
  requestLiveSession: RequestLiveSessionFn;
  setDisplayText: Dispatch<SetStateAction<string>>;
  setIsCallActive: (value: boolean) => void;
  setIsInVoiceCall: (value: boolean) => void;
  setIsMobileCall: (value: boolean) => void;
  setIsWebrtcCall: (value: boolean) => void;
  setLastHangupTime: (value: number) => void;
  setLiveAssistantText: Dispatch<SetStateAction<string>>;
  setLiveConnectRequested: Dispatch<SetStateAction<boolean>>;
  setLiveConnectionError: Dispatch<SetStateAction<string | null>>;
  setLiveStatus: (
    status: AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => void;
  setLiveTranscriptEventId: Dispatch<SetStateAction<number>>;
  setLiveUserTranscript: Dispatch<SetStateAction<string>>;
  setLiveUserTranscriptFinal: Dispatch<SetStateAction<boolean>>;
  setPromptPlaybackUnlocked: (value: boolean) => void;
  setRecord: Dispatch<SetStateAction<boolean>>;
  setSpeak: Dispatch<SetStateAction<boolean>>;
  setUserTalking: Dispatch<SetStateAction<boolean>>;
  socketHandlerRef: MutableRefObject<CamlinSocketHandler | null>;
  stopAudioPlayback: () => void;
  stopLiveSyntheticAudioSync: () => void;
}

export interface UseLiveListenControlsReturn {
  handleLivePrimaryButtonClick: () => Promise<void>;
  liveControlCaption: string;
  liveListenButtonActive: boolean;
  liveMuteButtonEnabled: boolean;
  livePrimaryButtonText: string;
  livePrimaryControlBusy: boolean;
  liveStatusLabel: string;
  liveVoiceSessionActive: boolean;
}

function canAutoRestartCapture({
  refs,
  activeListen,
  handleInputExternally,
  isVisualOnly,
  liveConnectRequested,
  liveModeActive,
  liveVoiceCallEnabled,
  playing,
  record
}: Pick<UseLiveListenControlsOptions, 'activeListen' | 'handleInputExternally' | 'isVisualOnly' | 'liveConnectRequested' | 'liveModeActive' | 'liveVoiceCallEnabled' | 'playing' | 'record'> & {
  refs: LiveListenControlRefs;
}): boolean {
  // Visual IVR (phone-driven) must never auto-restart browser STT — the
  // phone owns the microphone. See UseLiveListenControlsOptions.isVisualOnly.
  if (!usesBrowserSttOnLiveSurface({
    isVisualOnly,
    activeListen,
    liveVoiceCallEnabled,
    handleInputExternally
  })) {
    return false;
  }
  return (
    liveModeActive &&
    liveConnectRequested &&
    refs.liveAutoListenArmedRef.current &&
    refs.liveAutoListenPendingRef.current &&
    !refs.liveManualHangupRef.current &&
    Boolean(refs.liveClientRef.current) &&
    Boolean(refs.liveSessionContextRef.current) &&
    !record &&
    !playing &&
    !refs.liveSpeakingRef.current &&
    !refs.liveThinkingRef.current
  );
}

export function useLiveListenControls({
  refs,
  activeListen,
  avatarRef,
  currentSession,
  clearLiveAutoListenTimer,
  clearLivePlaybackCountdown,
  clearLiveSpeakingTimeout,
  clearLiveVoiceRetryTimer,
  dispatchAvatarEvent,
  ensureVoiceBrowserAccess,
  handleInputExternally,
  hangupDebounceTime,
  isAvatarReady,
  isCallActive,
  isInVoiceCall,
  isMobileCall,
  isVisualOnly,
  isVoiceInitialized,
  isWebrtcCall,
  lastHangupTime,
  liveConnectRequested,
  liveConnectionError,
  liveModeActive,
  liveSessionStatus,
  liveVoiceCallEnabled,
  playing,
  record,
  refreshUIButtonsOnCallEnd,
  requestLiveSession,
  setDisplayText,
  setIsCallActive,
  setIsInVoiceCall,
  setIsMobileCall,
  setIsWebrtcCall,
  setLastHangupTime,
  setLiveAssistantText,
  setLiveConnectRequested,
  setLiveConnectionError,
  setLiveStatus,
  setLiveTranscriptEventId,
  setLiveUserTranscript,
  setLiveUserTranscriptFinal,
  setPromptPlaybackUnlocked,
  setRecord,
  setSpeak,
  setUserTalking,
  socketHandlerRef,
  stopAudioPlayback,
  stopLiveSyntheticAudioSync
}: UseLiveListenControlsOptions): UseLiveListenControlsReturn {
  const liveStatusLabel = useMemo(() => {
    if (!liveModeActive) {
      return '';
    }

    switch (liveSessionStatus) {
      case 'connecting':
        return 'Connecting';
      case 'ready':
        return 'Ready';
      case 'listening':
        return 'Listening';
      case 'thinking':
        return 'Thinking';
      case 'speaking':
        return 'Speaking';
      case 'ended':
        return 'Ended';
      case 'error':
        return 'Error';
      default:
        return 'Live';
    }
  }, [liveModeActive, liveSessionStatus]);

  const liveListenButtonActive = liveModeActive && (liveSessionStatus === 'listening' || record);
  const liveVoiceSessionActive = liveModeActive && liveVoiceCallEnabled && (
    liveConnectRequested ||
    isCallActive ||
    isWebrtcCall ||
    isMobileCall ||
    isInVoiceCall
  );
  const liveWebsocketSessionReady = liveModeActive && !liveVoiceCallEnabled && liveConnectRequested;
  const liveMuteButtonEnabled = liveModeActive && liveVoiceCallEnabled && (
    isCallActive ||
    isWebrtcCall ||
    isMobileCall ||
    isInVoiceCall
  );
  const livePrimaryControlBusy = liveVoiceCallEnabled
    ? (!liveVoiceSessionActive && (!isAvatarReady || (liveConnectRequested && !isVoiceInitialized)))
    : (!isAvatarReady || record || liveSessionStatus === 'connecting');

  const stopLiveListening = useCallback(() => {
    const currentContext = refs.liveSessionContextRef.current;
    refs.liveManualHangupRef.current = true;
    refs.liveAutoListenArmedRef.current = false;
    setPromptPlaybackUnlocked(false);
    refs.liveAutoListenPendingRef.current = false;
    refs.liveVoicePromptStartedRef.current = false;
    refs.livePromptListeningRef.current = false;
    refs.liveSpeakingRef.current = false;
    refs.liveThinkingRef.current = false;
    refs.liveVoiceAttemptRef.current += 1;
    clearLiveVoiceRetryTimer();
    refs.liveVoiceRetryCountRef.current = 0;
    clearLiveAutoListenTimer();
    clearLivePlaybackCountdown();
    clearLiveSpeakingTimeout();
    refs.livePromptAudioRequestRef.current += 1;
    stopAudioPlayback();
    stopLiveSyntheticAudioSync();
    setLiveConnectRequested(false);
    setLiveConnectionError(null);
    dispatchAvatarEvent('avatar:listening', { listening: false });
    dispatchAvatarEvent('avatar:thinking', { thinking: false });

    if (currentSession) {
      hangupNow({
        currentSession,
        socketHandlerRef: socketHandlerRef as RefObject<CamlinSocketHandler>,
        avatarRef,
        refreshUIButtonsOnCallEnd,
        lastHangupTime,
        setLastHangupTime,
        hangupDebounceTime,
        setIsMobileCall,
        setIsWebrtcCall,
        setIsInVoiceCall
      } as any);
    } else {
      setIsCallActive(false);
      setIsMobileCall(false);
      setIsWebrtcCall(false);
      setIsInVoiceCall(false);
    }

    setLiveStatus('ready', currentContext);
  }, [
    avatarRef,
    clearLiveAutoListenTimer,
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    clearLiveVoiceRetryTimer,
    currentSession,
    dispatchAvatarEvent,
    hangupDebounceTime,
    lastHangupTime,
    refreshUIButtonsOnCallEnd,
    refs,
    setIsCallActive,
    setIsInVoiceCall,
    setIsMobileCall,
    setIsWebrtcCall,
    setLastHangupTime,
    setLiveConnectRequested,
    setLiveConnectionError,
    setLiveStatus,
    setPromptPlaybackUnlocked,
    socketHandlerRef,
    stopAudioPlayback,
    stopLiveSyntheticAudioSync
  ]);

  // Circuit breaker for runaway STT failures. If three consecutive
  // sttFromMic() calls throw (network down, mic permission revoked mid-call,
  // browser STT engine failed) we stop auto-listening so we don't burn
  // through speech recognition tokens / billing in a tight retry loop.
  // Originally landed in the monorepo mirror only (see commit 04dc5d49 in
  // camlin-nextgen "fix: live mode infinite speech token loop"); ported
  // back to canonical so the next mirror sync doesn't wipe it.
  const consecutiveCaptureFailuresRef = useMemo(() => ({ current: 0 }), []);
  const MAX_CONSECUTIVE_CAPTURE_FAILURES = 3;

  const startLiveSpeechCapture = useCallback(async () => {
    const currentContext = refs.liveSessionContextRef.current;
    const liveClient = refs.liveClientRef.current;

    // Visual IVR short-circuit. The phone owns mic+speaker on this surface,
    // so opening the browser microphone here would cause the laptop to pick
    // up phone audio bleed (or a nearby person's voice) and submit it to STT
    // as if it were the user speaking. Confirmed in production: a Visual IVR
    // session transcribed phone bleed as "Madonna thoroughly" and posted it
    // back to the runtime. Bail out before any MediaRecorder access.
    if (isVisualOnly) {
      refs.liveAutoListenArmedRef.current = false;
      refs.liveAutoListenPendingRef.current = false;
      refs.livePromptListeningRef.current = false;
      return;
    }

    if (!currentContext || !liveClient || !liveConnectRequested) {
      console.warn('[useLiveListenControls] speech capture deferred: live session not ready', {
        hasContext: Boolean(currentContext),
        hasClient: Boolean(liveClient),
        liveConnectRequested
      });
      requestLiveSession();
      return;
    }

    if (consecutiveCaptureFailuresRef.current >= MAX_CONSECUTIVE_CAPTURE_FAILURES) {
      console.warn(`[useLiveListenControls] Speech capture circuit breaker tripped after ${MAX_CONSECUTIVE_CAPTURE_FAILURES} consecutive failures — disabling auto-listen`);
      refs.liveAutoListenArmedRef.current = false;
      refs.liveAutoListenPendingRef.current = false;
      refs.livePromptListeningRef.current = false;
      setLiveConnectionError('Speech recognition unavailable. Check your connection and try again.');
      setLiveAssistantText('Speech recognition unavailable. Check your connection and try again.');
      setLiveStatus('error', currentContext);
      return;
    }

    let submittedInput = false;
    let captureFailed = false;
    if (usesBrowserSttOnLiveSurface({
      isVisualOnly,
      activeListen,
      liveVoiceCallEnabled,
      handleInputExternally
    })) {
      refs.liveAutoListenArmedRef.current = true;
    }
    refs.liveAutoListenPendingRef.current = false;
    clearLiveAutoListenTimer();
    refs.livePromptAudioRequestRef.current += 1;
    stopAudioPlayback();
    refs.liveManualHangupRef.current = false;
    refs.livePromptListeningRef.current = true;
    refs.liveThinkingRef.current = false;
    setLiveConnectionError(null);
    setLiveUserTranscript('');
    setLiveUserTranscriptFinal(false);
    dispatchAvatarEvent('avatar:thinking', { thinking: false });
    dispatchAvatarEvent('avatar:listening', { listening: true });
    setLiveStatus('listening', currentContext);
    setLiveAssistantText('Listening...');

    try {
      await sttFromMic(
        setDisplayText,
        setUserTalking,
        setRecord,
        setSpeak,
        async (text: string) => {
          const transcript = text.trim();
          if (!transcript) {
            return;
          }

          submittedInput = true;
          consecutiveCaptureFailuresRef.current = 0;
          refs.livePromptListeningRef.current = false;
          refs.liveThinkingRef.current = true;
          setLiveUserTranscript(transcript);
          setLiveUserTranscriptFinal(true);
          setLiveTranscriptEventId((prev) => prev + 1);
          dispatchAvatarEvent('avatar:transcript', { text: transcript, isFinal: true });
          dispatchAvatarEvent('avatar:listening', { listening: false });
          dispatchAvatarEvent('avatar:thinking', { thinking: true });
          setLiveAssistantText('Thinking...');
          setLiveStatus('thinking', currentContext);
          if (!handleInputExternally) {
            liveClient.sendInput(transcript);
          }
        },
        () => {},
        (interimText: string) => {
          setLiveUserTranscript(interimText);
          setLiveUserTranscriptFinal(false);
          dispatchAvatarEvent('avatar:transcript', { text: interimText, isFinal: false });
        }
      );
      consecutiveCaptureFailuresRef.current = 0;
    } catch (error) {
      captureFailed = true;
      consecutiveCaptureFailuresRef.current += 1;
      // Never swallow this silently: a dead mic with no console trace cost
      // a full debugging round on the Companion (6 Aug 2026).
      console.warn('[useLiveListenControls] live speech capture failed', error);
      const message = error instanceof Error ? error.message : 'Live speech capture is unavailable right now.';
      refs.liveAutoListenArmedRef.current = false;
      refs.liveAutoListenPendingRef.current = false;
      refs.livePromptListeningRef.current = false;
      refs.liveThinkingRef.current = false;
      dispatchAvatarEvent('avatar:listening', { listening: false });
      dispatchAvatarEvent('avatar:thinking', { thinking: false });
      setLiveConnectionError(message);
      setLiveAssistantText(message);
      setLiveStatus('ready', currentContext);
    }

    if (!submittedInput && !captureFailed) {
      const shouldAutoRestart = (
        usesBrowserSttOnLiveSurface({
          isVisualOnly,
          activeListen,
          liveVoiceCallEnabled,
          handleInputExternally
        }) &&
        refs.liveAutoListenArmedRef.current &&
        !refs.liveManualHangupRef.current &&
        liveConnectRequested
      );
      refs.livePromptListeningRef.current = shouldAutoRestart;
      refs.liveThinkingRef.current = false;
      refs.liveAutoListenPendingRef.current = shouldAutoRestart;
      dispatchAvatarEvent('avatar:listening', { listening: shouldAutoRestart });
      dispatchAvatarEvent('avatar:thinking', { thinking: false });
      setLiveAssistantText(shouldAutoRestart ? 'Listening...' : (refs.livePromptTextRef.current || 'Ready when you are.'));
      setLiveStatus(shouldAutoRestart ? 'listening' : 'ready', currentContext);
    }
  }, [
    activeListen,
    clearLiveAutoListenTimer,
    dispatchAvatarEvent,
    handleInputExternally,
    isVisualOnly,
    liveConnectRequested,
    liveVoiceCallEnabled,
    refs,
    requestLiveSession,
    setDisplayText,
    setLiveAssistantText,
    setLiveConnectionError,
    setLiveStatus,
    setLiveTranscriptEventId,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal,
    setRecord,
    setSpeak,
    setUserTalking,
    stopAudioPlayback
  ]);

  useEffect(() => {
    const shouldAutoRestart = canAutoRestartCapture({
      refs,
      activeListen,
      handleInputExternally,
      isVisualOnly,
      liveConnectRequested,
      liveModeActive,
      liveVoiceCallEnabled,
      playing,
      record
    });

    if (!shouldAutoRestart) {
      clearLiveAutoListenTimer();
      return;
    }

    if (refs.liveAutoListenTimerRef.current) {
      return;
    }

    refs.liveAutoListenTimerRef.current = window.setTimeout(() => {
      refs.liveAutoListenTimerRef.current = null;
      const canRestart = canAutoRestartCapture({
        refs,
        activeListen,
        handleInputExternally,
        isVisualOnly,
        liveConnectRequested,
        liveModeActive,
        liveVoiceCallEnabled,
        playing,
        record
      });
      if (!canRestart) {
        return;
      }

      refs.liveAutoListenPendingRef.current = false;
      void startLiveSpeechCapture();
    }, 250);
  }, [
    activeListen,
    clearLiveAutoListenTimer,
    handleInputExternally,
    isVisualOnly,
    liveConnectRequested,
    liveModeActive,
    liveSessionStatus,
    liveVoiceCallEnabled,
    playing,
    record,
    refs,
    startLiveSpeechCapture
  ]);

  // Voice turn armed while the browser's mic permission prompt is up
  // (item 9): consumed by the `avatar:mic-access-granted` effect below.
  const pendingVoiceTurnAfterGrantRef = useRef(false);

  const handleLivePrimaryButtonClick = useCallback(async () => {
    // Breadcrumb for hosts debugging a dead mic button: which shape and
    // which branch this click resolves to. One line per tap, cheap.
    console.log('[useLiveListenControls] primary action', {
      liveVoiceSessionActive,
      liveVoiceCallEnabled,
      liveWebsocketSessionReady,
      activeListen,
      handleInputExternally: Boolean(handleInputExternally)
    });
    if (liveVoiceSessionActive) {
      stopLiveListening();
      return;
    }

    const accessReady = await ensureVoiceBrowserAccess(
      liveVoiceCallEnabled || liveWebsocketSessionReady || (activeListen && !liveVoiceCallEnabled)
    );
    if (!accessReady) {
      // Keep the turn ARMED across the browser permission prompt (Keith
      // item 9, 6 Aug 2026). On a first visit the permission dialog often
      // outlives the access check's grace window; when the visitor does
      // hit Allow, useVoiceBrowserAccess fires `avatar:mic-access-granted`
      // and the effect below replays this action so capture starts
      // immediately — the tap no longer dies at the prompt.
      pendingVoiceTurnAfterGrantRef.current = true;
      // Let UI owners (Companion mic button) keep their "starting" state
      // through the prompt instead of resetting to idle.
      window.dispatchEvent(new CustomEvent('avatar:voice-turn-pending'));
      console.warn('[useLiveListenControls] primary action stopped: browser access not granted (turn stays armed for a late grant)');
      return;
    }
    pendingVoiceTurnAfterGrantRef.current = false;

    if (usesBrowserSttOnLiveSurface({
      isVisualOnly,
      activeListen,
      liveVoiceCallEnabled,
      handleInputExternally
    })) {
      refs.liveAutoListenArmedRef.current = true;
      refs.liveManualHangupRef.current = false;
    }

    // Always unlock prompt playback on the user gesture, regardless of
    // activeListen. Phase 4 surfaces (AvatarCallSurface) suppress the
    // legacy LiveControlDock by setting activeListen: false but still
    // need bot-prompt audio to play. Without this, the call connects,
    // mic is open, but no audio comes back from the avatar (silent
    // call). The unlock is gated by the user gesture (this click) so
    // it's still safe under autoplay policies.
    setPromptPlaybackUnlocked(true);
    refs.liveManualHangupRef.current = false;

    if (!liveVoiceCallEnabled && liveWebsocketSessionReady) {
      await startLiveSpeechCapture();
      return;
    }

    requestLiveSession();
  }, [
    activeListen,
    ensureVoiceBrowserAccess,
    handleInputExternally,
    isVisualOnly,
    liveVoiceCallEnabled,
    liveVoiceSessionActive,
    liveWebsocketSessionReady,
    requestLiveSession,
    setPromptPlaybackUnlocked,
    startLiveSpeechCapture,
    stopLiveListening
  ]);

  useEffect(() => {
    const handlePrimaryActionRequest = () => {
      void handleLivePrimaryButtonClick();
    };

    window.addEventListener('avatar:live:primaryAction', handlePrimaryActionRequest as EventListener);
    return () => {
      window.removeEventListener('avatar:live:primaryAction', handlePrimaryActionRequest as EventListener);
    };
  }, [handleLivePrimaryButtonClick]);

  // Late microphone grant (item 9): the visitor hit Allow after the access
  // check gave up (or via the retry chip). If a voice turn is still armed,
  // replay the primary action — the freshly cached stream makes the access
  // check pass instantly and capture starts straight away.
  useEffect(() => {
    const handleMicGranted = () => {
      if (!pendingVoiceTurnAfterGrantRef.current) {
        return;
      }
      pendingVoiceTurnAfterGrantRef.current = false;
      console.log('[useLiveListenControls] mic granted after prompt — resuming armed voice turn');
      void handleLivePrimaryButtonClick();
    };

    window.addEventListener('avatar:mic-access-granted', handleMicGranted);
    return () => {
      window.removeEventListener('avatar:mic-access-granted', handleMicGranted);
    };
  }, [handleLivePrimaryButtonClick]);

  useEffect(() => {
    const handleSpeakRequest = (event: Event) => {
      const detail = (event as CustomEvent<{ text?: string; voiceId?: string }>).detail;
      const text = typeof detail?.text === 'string' ? detail.text.trim() : '';
      if (!text) {
        return;
      }
      // Drive the avatar's emoji/keyword expression overlay BEFORE shipping
      // the text to the runtime. Two reasons:
      //   1. The runtime's `sanitizeForVoice` strips emoji presentation
      //      characters before forwarding to TTS (so Azure TTS does NOT
      //      read out "smiling face with open mouth" as part of the
      //      utterance). After that strip, the text the runtime echoes
      //      back via `visual.prompt` no longer contains the emoji,
      //      so the existing `useLiveVisualChannel.onPrompt` path that
      //      mirrors `avatar:chat:content` would never see the emoji
      //      either.
      //   2. The host already has the original text (with emoji) in
      //      hand at the moment of dispatch; we should let Avatar.tsx
      //      pick its expression from that, in parallel with shipping
      //      the cleaned text to TTS. The two effects (no spoken
      //      "smiling face with open mouth" + a real smile animation)
      //      together are the thing the user is asking for.
      // Ports the `avatar:chat:content` mirror that `useLiveVisualChannel`
      // already does for runtime-LLM-driven prompts.
      dispatchAvatarEvent('avatar:chat:content', {
        text,
        source: 'bot',
        origin: 'host-speak'
      });
      (refs.liveClientRef.current as any)?.sendSpeak?.(text, detail?.voiceId);
    };

    window.addEventListener('avatar:live:speak', handleSpeakRequest as EventListener);
    return () => {
      window.removeEventListener('avatar:live:speak', handleSpeakRequest as EventListener);
    };
  }, [dispatchAvatarEvent, refs]);

  // Mirror of the avatar:live:speak wiring for the STOP direction
  // (Aug 29 2026 — live-call stop-speaking control). Dispatched by
  // SpeechPlaybackControl's live stop button (and available to hosts);
  // forwards to LiveModeClient.sendStopSpeaking(), which ships
  // `visual.playback.stop` over /visual-channel. The runtime bargeins
  // the voice leg and broadcasts visual.playback.stopped back, so the
  // face settles through the existing onSpeaking(false) path.
  useEffect(() => {
    const handleStopSpeakingRequest = () => {
      (refs.liveClientRef.current as any)?.sendStopSpeaking?.();
    };
    window.addEventListener('avatar:live:stopSpeaking', handleStopSpeakingRequest);
    return () => {
      window.removeEventListener('avatar:live:stopSpeaking', handleStopSpeakingRequest);
    };
  }, [refs]);

  // Slice M4h-6d-4 (May 12 2026) — listen for the "Confirm identity"
  // button click dispatched by the host shell modal. Forwards to the
  // LiveModeClient which sends `visual.identity.confirmed` on the
  // /visual-channel WS so the runtime gate releases.
  //
  // Anti-pattern: do NOT debounce this listener. The runtime's
  // `releaseIdentityGate` is idempotent (additional events after the
  // first are no-ops), and a debounce risks dropping a deliberate
  // re-click if the user thinks the first didn't register.
  useEffect(() => {
    const handleIdentityConfirm = () => {
      const client = refs.liveClientRef.current as any;
      if (!client?.sendIdentityConfirmed) {
        console.warn('[LiveMode] avatar:identity:confirm received but liveClient is not ready');
        return;
      }
      console.log('[LiveMode] avatar:identity:confirm — forwarding to runtime');
      client.sendIdentityConfirmed();
    };
    window.addEventListener('avatar:identity:confirm', handleIdentityConfirm);
    return () => {
      window.removeEventListener('avatar:identity:confirm', handleIdentityConfirm);
    };
  }, [refs]);

  useEffect(() => {
    const handlePromptInputRequest = (event: Event) => {
      const detail = (event as CustomEvent<{ value?: string; label?: string }>).detail;
      const value = typeof detail?.value === 'string' ? detail.value.trim() : '';
      const label = typeof detail?.label === 'string' ? detail.label.trim() : '';
      if (!value) {
        return;
      }

      if (!handleInputExternally) {
        refs.liveClientRef.current?.sendInput(value, label ? { label } : undefined);
      }
    };

    window.addEventListener('avatar:live:promptInput', handlePromptInputRequest as EventListener);
    return () => {
      window.removeEventListener('avatar:live:promptInput', handlePromptInputRequest as EventListener);
    };
  }, [refs]);

  // Team mode (TM-2, Jul 31 2026) — public window-event bridge for mid-session
  // context updates. A host living outside the SDK's own React tree (e.g.
  // AgentLink's agents panel) has no way to reach `liveClient` directly, and
  // re-mounting the embed to pass new config would tear down the live WebRTC
  // session. This event lets a host re-scope the conversation ("the user is
  // now asking about agent X") without touching the mic or the mount key —
  // it is a pure WebSocket send via `sendSessionContext`, which already
  // queues-and-replays around the `visual.linked` handshake and whose
  // runtime-side merge is idempotent. See
  // `avatar-nextgen/src/core/hooks/useLiveListenControls.ts` in
  // `camlin-workspace/docs/AVATAR_TEAM_MODE_PLAN_JUL_30_2026.md` §3/§11.
  //
  // Additive: no existing event, prop, type, or component changes. A host
  // that never dispatches this sees no behavioural difference. Do not add a
  // second event for the same purpose — this is the one and only mid-session
  // context-update bridge; extend `detail` rather than forking a new event.
  useEffect(() => {
    const handleContextUpdateRequest = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown> | undefined>).detail;
      if (!detail || typeof detail !== 'object') {
        return;
      }
      refs.liveClientRef.current?.sendSessionContext?.(detail);
    };

    window.addEventListener('avatar:live:contextUpdate', handleContextUpdateRequest as EventListener);
    return () => {
      window.removeEventListener('avatar:live:contextUpdate', handleContextUpdateRequest as EventListener);
    };
  }, [refs]);

  const livePrimaryButtonText = useMemo(() => {
    if (liveVoiceSessionActive) {
      return liveSessionStatus === 'connecting' ? 'Stop Connecting' : 'Stop Listening';
    }

    if (record) {
      return 'Listening';
    }

    if (!isAvatarReady) {
      return 'Loading Avatar';
    }

    if (!liveVoiceCallEnabled && liveSessionStatus === 'connecting') {
      return 'Connecting';
    }

    if (liveVoiceCallEnabled && liveConnectRequested && !isVoiceInitialized) {
      return 'Connecting Voice';
    }

    return 'Listen';
  }, [
    isAvatarReady,
    isVoiceInitialized,
    liveConnectRequested,
    liveSessionStatus,
    liveVoiceCallEnabled,
    liveVoiceSessionActive,
    record
  ]);

  const liveControlCaption = useMemo(() => {
    if (!liveModeActive) {
      return '';
    }

    if (liveConnectionError) {
      return 'Live Error';
    }

    if (!liveVoiceCallEnabled) {
      if (record) {
        return 'Listening';
      }

      switch (liveSessionStatus) {
        case 'connecting':
          return 'Connecting';
        case 'thinking':
          return 'Thinking';
        case 'speaking':
          return 'Speaking';
        default:
          return 'Not Listening';
      }
    }

    if (!liveVoiceSessionActive) {
      return 'Not Listening';
    }

    switch (liveSessionStatus) {
      case 'connecting':
        return 'Connecting';
      case 'thinking':
        return 'Thinking';
      case 'speaking':
        return 'Speaking';
      case 'listening':
        return 'Listening';
      default:
        return 'Live Call Active';
    }
  }, [
    liveConnectionError,
    liveModeActive,
    liveSessionStatus,
    liveVoiceCallEnabled,
    liveVoiceSessionActive,
    record
  ]);

  return {
    handleLivePrimaryButtonClick,
    liveControlCaption,
    liveListenButtonActive,
    liveMuteButtonEnabled,
    livePrimaryButtonText,
    livePrimaryControlBusy,
    liveStatusLabel,
    liveVoiceSessionActive
  };
}

export default useLiveListenControls;
